<template>
  <div class="resource-page financial-resource-page">
    <section class="resource-toolbar">
      <div>
        <h2>Fluxo de caixa</h2>
        <p>Entradas, saídas e saldo projetado com base em parcelas e movimentações financeiras.</p>
      </div>
      <div class="toolbar-actions">
        <v-btn icon :loading="loading" title="Atualizar" @click="load">
          <v-icon size="21">mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <section class="finance-filter-row">
      <v-select
        v-model="filters.period"
        :items="periodOptions"
        dense
        flat
        solo
        hide-details
        class="finance-filter-field period-field"
        @change="onPeriodChange"
      />
      <v-menu v-model="dateRangeMenu" offset-y left max-width="360" :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="date-button" :disabled="filters.period !== 'custom'" v-bind="attrs" v-on="on">
            <v-icon size="18">mdi-calendar-range-outline</v-icon>
          </v-btn>
        </template>

        <v-card class="filter-card">
          <v-card-title>Intervalo personalizado</v-card-title>
          <v-card-text>
            <v-text-field v-model="filters.start_date" label="Data inicial" type="date" outlined dense hide-details="auto" class="mb-3" />
            <v-text-field v-model="filters.end_date" label="Data final" type="date" outlined dense hide-details="auto" />
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="clearCustomRange">Limpar</v-btn>
            <v-spacer />
            <v-btn depressed color="primary" @click="applyCustomRange">Aplicar</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
      <v-select
        v-model="filters.group_by"
        :items="groupOptions"
        label="Agrupar por"
        dense
        flat
        solo
        hide-details
        class="finance-filter-field group-field"
        @change="load"
      />
      <v-select
        v-model="filters.financial_account_id"
        :items="accountOptions"
        label="Conta financeira"
        dense
        flat
        solo
        clearable
        hide-details
        class="finance-filter-field account-field"
        @change="load"
      />
    </section>

    <section class="finance-summary-strip">
      <div>
        <span>Saldo inicial efetivo</span>
        <strong>{{ money(cashFlow.effective_opening_balance) }}</strong>
      </div>
      <div>
        <span>Entradas</span>
        <strong class="positive">{{ money(totalInflow) }}</strong>
      </div>
      <div>
        <span>Saídas</span>
        <strong class="negative">{{ money(totalOutflow) }}</strong>
      </div>
      <div>
        <span>Saldo projetado final</span>
        <strong>{{ money(cashFlow.totals.final_projected_balance) }}</strong>
      </div>
    </section>

    <section class="table-panel">
      <BoaruaDataTable
        :headers="headers"
        :items="cashFlow.rows"
        :loading="loading"
        item-key="key"
        class="financial-table"
      >
        <template v-slot:item.label="{ item }">
          {{ periodDisplay(item) }}
        </template>
        <template v-slot:item.realized_inflow="{ item }">
          <strong class="positive">{{ money(item.realized_inflow) }}</strong>
        </template>
        <template v-slot:item.realized_outflow="{ item }">
          <strong class="negative">{{ money(item.realized_outflow) }}</strong>
        </template>
        <template v-slot:item.planned_inflow="{ item }">
          {{ money(item.planned_inflow) }}
        </template>
        <template v-slot:item.planned_outflow="{ item }">
          {{ money(item.planned_outflow) }}
        </template>
        <template v-slot:item.projected_balance="{ item }">
          <strong>{{ money(item.projected_balance) }}</strong>
        </template>
      </BoaruaDataTable>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import BoaruaDataTable from "@/views/boarua/components/BoaruaDataTable.vue";

const emptyCashFlow = () => ({
  effective_opening_balance: 0,
  totals: {
    realized_inflow: 0,
    realized_outflow: 0,
    planned_inflow: 0,
    planned_outflow: 0,
    final_projected_balance: 0,
  },
  rows: [],
});

export default {
  name: "FinancialCashFlow",
  components: { BoaruaDataTable },
  data() {
    const now = new Date();
    const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);

    return {
      loading: false,
      loadSequence: 0,
      dateRangeMenu: false,
      cashFlow: emptyCashFlow(),
      accounts: [],
      filters: {
        period: "current_month",
        start_date: start,
        end_date: end,
        group_by: "month",
        financial_account_id: null,
      },
      periodOptions: [
        { text: "Todo período", value: "all" },
        { text: "Mês atual", value: "current_month" },
        { text: "Últimos 3 meses", value: "last_3_months" },
        { text: "Últimos 6 meses", value: "last_6_months" },
        { text: "Ano atual", value: "current_year" },
        { text: "Personalizado", value: "custom" },
      ],
      groupOptions: [
        { text: "Dia", value: "day" },
        { text: "Semana", value: "week" },
        { text: "Mês", value: "month" },
      ],
      headers: [
        { text: "Período", value: "label", width: 170 },
        { text: "Entradas realizadas", value: "realized_inflow" },
        { text: "Saídas realizadas", value: "realized_outflow" },
        { text: "Entradas previstas", value: "planned_inflow" },
        { text: "Saídas previstas", value: "planned_outflow" },
        { text: "Saldo projetado", value: "projected_balance" },
      ],
    };
  },
  computed: {
    accountOptions() {
      return this.accounts.map((account) => ({ text: account.name, value: account.id }));
    },
    totalInflow() {
      const totals = this.cashFlow.totals || {};
      return Number(totals.realized_inflow || 0) + Number(totals.planned_inflow || 0);
    },
    totalOutflow() {
      const totals = this.cashFlow.totals || {};
      return Number(totals.realized_outflow || 0) + Number(totals.planned_outflow || 0);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      const sequence = this.loadSequence + 1;
      this.loadSequence = sequence;
      this.loading = true;
      try {
        const params = Object.assign({}, this.filters);
        delete params.period;
        const [cashFlowResponse, accountsResponse] = await Promise.all([
          axios.get("/financial/cash-flow", { params }),
          axios.get("/financial/accounts", { params: { limit: 100, status: "active" } }),
        ]);
        if (sequence !== this.loadSequence) return;
        this.cashFlow = Object.assign(emptyCashFlow(), cashFlowResponse.data.content || {});
        const accountsContent = accountsResponse.data.content || {};
        this.accounts = accountsContent.data || accountsContent || [];
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível carregar o fluxo de caixa");
      } finally {
        if (sequence === this.loadSequence) this.loading = false;
      }
    },
    onPeriodChange(period) {
      if (period === "custom") {
        if (!this.filters.start_date || !this.filters.end_date) {
          this.filters.start_date = this.monthStart(new Date());
          this.filters.end_date = this.toDateInput(new Date());
        }
        this.dateRangeMenu = true;
        return;
      }

      this.dateRangeMenu = false;
      const range = this.periodRange(period);
      this.filters.start_date = range.start;
      this.filters.end_date = range.end;
      this.load();
    },
    applyCustomRange() {
      this.dateRangeMenu = false;
      this.load();
    },
    clearCustomRange() {
      this.filters.period = "all";
      this.filters.start_date = "";
      this.filters.end_date = "";
      this.dateRangeMenu = false;
      this.load();
    },
    periodRange(period) {
      const now = new Date();
      if (period === "all") return { start: "2000-01-01", end: this.monthEnd(now) };
      if (period === "last_3_months") return { start: this.monthStart(this.addMonths(now, -2)), end: this.monthEnd(now) };
      if (period === "last_6_months") return { start: this.monthStart(this.addMonths(now, -5)), end: this.monthEnd(now) };
      if (period === "current_year") return { start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` };
      return { start: this.monthStart(now), end: this.monthEnd(now) };
    },
    addMonths(date, amount) {
      const next = new Date(date.getFullYear(), date.getMonth(), 1);
      next.setMonth(next.getMonth() + amount);
      return next;
    },
    monthStart(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
    },
    monthEnd(date) {
      return this.toDateInput(new Date(date.getFullYear(), date.getMonth() + 1, 0));
    },
    toDateInput(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    periodDisplay(item = {}) {
      if (this.filters.group_by === "day") return this.formatDate(item.key || item.label);
      if (this.filters.group_by === "week") return `Semana de ${this.formatDate(item.start_date || item.key)}`;
      return this.formatMonth(item.key || item.label);
    },
    formatDate(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[3]}/${match[2]}/${match[1]}`;
      return value || "-";
    },
    formatMonth(value) {
      const match = String(value || "").match(/^(\d{4})-(\d{2})/);
      if (match) return `${match[2]}/${match[1]}`;
      return value || "-";
    },
    money(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    notifyError(message) {
      this.$dialog && this.$dialog.notify && this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
    },
  },
};
</script>

<style scoped lang="scss">
.financial-resource-page {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.resource-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 22px;
    font-weight: 900;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.finance-filter-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.finance-filter-field {
  min-width: 0;
  flex: 1 1 0;
}

.finance-filter-field.period-field {
  flex: 1.15 1 210px;
}

.finance-filter-field.group-field {
  flex: .85 1 150px;
}

.finance-filter-field.account-field {
  flex: 1.2 1 220px;
}

.finance-filter-row::v-deep {
  .v-input__slot {
    min-height: 42px !important;
    background: #f4f7f4 !important;
    border-radius: 999px !important;
    box-shadow: none !important;
    padding: 0 18px !important;
  }

  .v-label,
  input,
  .v-select__selection {
    color: #66736b !important;
    font-size: 14px;
  }
}

.date-button {
  flex: 0 0 auto;
  width: 42px !important;
  height: 42px !important;
  background-color: #f4f7f4 !important;
  border: 1px solid transparent;
  color: #66736b !important;
  box-shadow: none !important;
}

.date-button.v-btn--disabled {
  opacity: .55;
}

.filter-card {
  border-radius: 8px;

  .v-card__title {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
  }
}

.finance-summary-strip {
  min-height: 54px;
  padding: 10px 18px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;

  div {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }

  span {
    color: #66736b;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
  }

  strong {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
    white-space: nowrap;
  }
}

.positive {
  color: #057a3d;
}

.negative {
  color: #b42318;
}

.table-panel {
  padding: 0;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
  overflow: hidden;
}

.financial-table::v-deep {
  .v-data-table__wrapper {
    overflow-x: auto;
  }

  table {
    min-width: 900px;
  }
}

@media (max-width: 1280px) {
  .finance-summary-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .finance-summary-strip div {
    display: grid;
    gap: 4px;
  }

  .finance-summary-strip span,
  .finance-summary-strip strong {
    white-space: normal;
  }

}

@media (max-width: 700px) {
  .finance-filter-row {
    flex-wrap: wrap;
  }

  .finance-filter-field,
  .finance-filter-field.group-field,
  .finance-filter-field.period-field,
  .finance-filter-field.account-field {
    flex: 1 1 220px;
  }

  .finance-summary-strip {
    grid-template-columns: 1fr;
  }

  .finance-summary-strip div {
    display: flex;
    justify-content: space-between;
  }

  .resource-toolbar,
  .finance-filter-row {
    align-items: stretch;
    flex-direction: column;
  }

  .finance-filter-field,
  .finance-filter-field.group-field,
  .finance-filter-field.period-field,
  .finance-filter-field.account-field,
  .date-button {
    width: 100%;
    flex: 1 1 auto;
  }
}
</style>
